import { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useHistoricalData, aggregateZillowByMonth, transformRedfinForChart } from '../hooks/useHistoricalData';
import './HistoricalTrendsChart.css';

interface HistoricalTrendsChartProps {
  market: string;
  title?: string;
  height?: number;
  showRentals?: boolean;
  showHousing?: boolean;
}

export function HistoricalTrendsChart({
  market,
  title = 'Historical Market Trends',
  height = 350,
  showRentals = true,
  showHousing = true,
}: HistoricalTrendsChartProps) {
  const { loading, error, zillow, redfin, regionName } = useHistoricalData(market);

  // Transform data for charts
  const rentalData = useMemo(() => aggregateZillowByMonth(zillow), [zillow]);
  const housingData = useMemo(() => transformRedfinForChart(redfin), [redfin]);

  // Format helpers
  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  if (loading) {
    return (
      <div className="historical-chart-container">
        <h3 className="chart-title">{title}</h3>
        <div className="chart-loading">Loading historical data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="historical-chart-container">
        <h3 className="chart-title">{title}</h3>
        <div className="chart-error">{error}</div>
      </div>
    );
  }

  const hasRentalData = rentalData.length > 0;
  const hasHousingData = housingData.length > 0;

  if (!hasRentalData && !hasHousingData) {
    return (
      <div className="historical-chart-container">
        <h3 className="chart-title">{title}</h3>
        <div className="chart-empty">
          No historical data available for this market.
          {regionName && <span className="region-hint"> (Region: {regionName})</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="historical-chart-container">
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>
        {regionName && <span className="region-name">{regionName}</span>}
      </div>

      {showRentals && hasRentalData && (
        <div className="chart-section">
          <h4>Rental Market Trends (Zillow)</h4>
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={rentalData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                stroke="var(--border)"
              />
              <YAxis
                yAxisId="rent"
                tickFormatter={formatCurrency}
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                stroke="var(--border)"
                domain={['auto', 'auto']}
              />
              <YAxis
                yAxisId="yoy"
                orientation="right"
                tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                stroke="var(--border)"
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                }}
                labelFormatter={formatDate}
                formatter={(value, name) => {
                  const numVal = value as number;
                  if (name === 'Median Rent') return [formatCurrency(numVal), name];
                  if (name === 'YoY Change') return [`${(numVal * 100).toFixed(1)}%`, name];
                  return [numVal, name];
                }}
              />
              <Legend />
              <Line
                yAxisId="rent"
                type="monotone"
                dataKey="medianRent"
                name="Median Rent"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="yoy"
                type="monotone"
                dataKey="rentYoy"
                name="YoY Change"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="chart-stats">
            <span>Data points: {rentalData.length}</span>
            {rentalData.length > 0 && (
              <>
                <span>Range: {formatDate(rentalData[0].date)} - {formatDate(rentalData[rentalData.length - 1].date)}</span>
              </>
            )}
          </div>
        </div>
      )}

      {showHousing && hasHousingData && (
        <div className="chart-section">
          <h4>Housing Market Trends (Redfin)</h4>
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={housingData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                stroke="var(--border)"
              />
              <YAxis
                yAxisId="price"
                tickFormatter={formatCurrency}
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                stroke="var(--border)"
                domain={['auto', 'auto']}
              />
              <YAxis
                yAxisId="supply"
                orientation="right"
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                stroke="var(--border)"
                domain={[0, 'auto']}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                }}
                labelFormatter={formatDate}
                formatter={(value, name) => {
                  const numVal = value as number;
                  if (name === 'Sale Price') return [formatCurrency(numVal), name];
                  if (name === 'Months Supply') return [numVal?.toFixed(1), name];
                  return [numVal?.toLocaleString(), name];
                }}
              />
              <Legend />
              <Line
                yAxisId="price"
                type="monotone"
                dataKey="medianSalePrice"
                name="Sale Price"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="supply"
                type="monotone"
                dataKey="monthsOfSupply"
                name="Months Supply"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="chart-stats">
            <span>Data points: {housingData.length}</span>
            {housingData.length > 0 && (
              <>
                <span>Range: {formatDate(housingData[0].date)} - {formatDate(housingData[housingData.length - 1].date)}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
